<template>
  <div class="calcFormsForm">
    <div v-if="loading">
      <v-progress-circular
        :size="20"
        color="primary"
        class="mr-2"
        indeterminate
      ></v-progress-circular>
      Loading...
    </div>
    <div v-else-if="lookupItem">
      <v-form ref="lookupForm" v-model="isFormValid" lazy-validation>
        <div>
          <v-text-field
            v-model="lookupItem.Name"
            maxlength="32"
            hint="Field max length is 32"
            label="Name"
            :rules="rules"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-text-field>
        </div>
        <div>
          <v-text-field
            v-model="lookupItem.Description"
            maxlength="150"
            hint="Field max length is 150"
            label="Description"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-text-field>
        </div>
        <div>
          <v-select
            v-model="lookupItem.TableName"
            label="Source name"
            :items="sourceList"
            :readonly="!showFormButtons"
            dense
            outlined
            @change="onSourceChange"
          ></v-select>
        </div>
        <div>
          <v-select
            label="Interpolation"
            v-model="lookupItem.Interpolation"
            :items="interpolationList"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-select>
        </div>
        <div>
          <v-select
            label="Interpolation field"
            v-model="lookupItem.InterpolationField"
            :items="fieldsList"
            :loading="loadingTableFields"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-select>
        </div>
        <div>
          <v-select
            label="Return field"
            v-model="lookupItem.ReturnValue"
            :items="fieldsList"
            :loading="loadingTableFields"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-select>
        </div>
        <div>
          <v-text-field
            v-model="lookupItem.MinimumItem"
            label="Minimum value"
            append-icon="mdi-arrow-down-bold-box"
            :readonly="!showFormButtons"
            @click:append="openCalculationSelection('MinimumItem')"
            dense
            outlined
          ></v-text-field>
        </div>
      </v-form>
      <div v-if="lookupItem.Fields.length" class="lookupTable">
        <v-simple-table class="mb-6">
          <tr>
            <th class="bd-b">Field name</th>
            <th class="bd-b">Operator</th>
            <th class="bd-b">Value</th>
          </tr>
          <tr v-for="(row, inx) in lookupItem.Fields" :key="inx">
            <td class="bd-b">{{ row.FieldName }}</td>
            <td class="bd-b" @click="editOperator(row)">
              {{ row.Condition }}
            </td>
            <td class="bd-b" @click="openCalculationSelection('Fields', inx)">
              {{ row.Value }}
            </td>
          </tr>
        </v-simple-table>
      </div>
      <div v-if="showFormButtons" class="formFooter">
        <div class="btnContainer">
          <v-btn v-if="lookupId" color="error" outlined @click="onDelete" class="mr-4">
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="onTest" class="mr-4"> Test </v-btn>
          <v-btn color="primary" outlined @click="onCancel" class="mr-4"> Cancel </v-btn>
          <v-btn color="primary" depressed @click="onSave"> Save </v-btn>
        </div>
      </div>
    </div>
    <div v-else>
      <v-alert type="info" outlined dense>No info found for this element</v-alert>
    </div>
    <Dialog ref="confirmationDialog" v-model="dialog"></Dialog>
    <CalculationSelection ref="calculationSelection" :levelId="planId" @select="onSelect" />

    <v-dialog v-model="operatorDialog" @keydown.esc="cancel" persistent width="500">
      <v-card>
        <div class="px-4 pt-8">
          <v-form>
            <div>
              <v-select
                v-model="editingTableRow.Condition"
                :items="operatorList"
                label="Operator"
                outlined
                dense
              ></v-select>
            </div>
          </v-form>
        </div>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="cancelOperatorEdit"> Cancel </v-btn>
          <v-btn color="primary" depressed @click.native="operatorDialog = false"> Ok </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';
import CalculationSelection from './Popups/CalculationSelection.vue';

export default {
  name: 'LookupForm',
  components: {
    Dialog,
    CalculationSelection,
  },
  props: {
    lookupId: { type: Number },
    planId: { type: Number },
  },

  data() {
    return {
      loading: false,
      loadingTableFields: false,
      isFormValid: false,
      dialog: false,
      lookupItem: null,
      rules: [(value) => !!value || 'Required.'],

      calculationSelectionProperty: null,
      calculationSelectionIndex: null,

      sourceList: ['GeneralRates'],
      interpolationList: [
        'None',
        'Nearest',
        'Lowest',
        'Highest',
        'Interpolated',
        'Calculated Rate',
        'Calculated Rate (monthly)',
        'Simple Rate',
        'Simple Rate (monthly)',
      ],

      operatorDialog: false,
      operatorList: ['=', '>', '<', '>=', '<='],
      editingTableRow: {},
      prevOperator: null,
      fieldsList: [],
    };
  },

  computed: {
    levelsList() {
      return [
        { text: 'System', value: 0 },
        { text: 'Plan', value: this.currentPlanId },
      ];
    },
    selectedLevel() {
      if (this.lookupItem) {
        return this.lookupItem.PlanId;
      }
      return null;
    },
    tableName() {
      if (this.lookupItem) return this.lookupItem.TableName;
      return null;
    },
    showFormButtons() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
  },

  watch: {
    lookupId: function () {
      if (this.lookupId) this.getForm();
      else this.createNewForm();

      this.getAvailableInputs();
    },
    selectedLevel() {
      this.getAvailableInputs();
    },
    tableName() {
      if (this.tableName){
        this.getLookupTableFields();
      } 
    },
  },

  created() {
    if (this.lookupId) this.getForm();
    else this.createNewForm();

    if (this.selectedLevel) this.getAvailableInputs();

    this.getSourceList();
  },

  methods: {
    createNewForm() {
      this.lookupItem = {
        TableName: '',
        ReturnValue: '',
        Interpolation: '',
        InterpolationField: '',
        MinimumItem: '',
        MinimumValue: '',
        Fields: [],
        CalcId: 0,
        PlanId: this.planId,
        GroupId: 0,
        Name: '',
        Description: '',
        CalcType: '',
        Expression: '',
        Rounding: '',
        RoundAmount: 0,
        RoundPeriod: '',
        Tag: '',
        Level: '',
        Username: '',
        DateChanged: '0001-01-01T00:00:00.000Z',
      };
    },

    onCancel() {
      if (this.lookupId) this.getForm();
      else this.createNewForm();
    },

    onSave() {
      const isFormValid = this.$refs.lookupForm.validate();
      if (isFormValid) this.saveData();
    },

    onSourceChange(){
      this.lookupItem.Fields = [];
    },

    onTest() {
      this.$emit('setTestMode');
    },

    openCalculationSelection(property, index) {
      if (index != null) this.calculationSelectionIndex = index;
      this.calculationSelectionProperty = property;
      this.$refs.calculationSelection.open();
    },

    onSelect(val) {
      if (this.calculationSelectionIndex != null) {
        this.lookupItem[this.calculationSelectionProperty][this.calculationSelectionIndex].Value =
          val;
      } else {
        this.lookupItem[this.calculationSelectionProperty] = val;
      }
      this.calculationSelectionIndex = null;
      this.calculationSelectionProperty = null;
    },

    editOperator(row) {
      if (!this.showFormButtons) return;
      this.operatorDialog = true;
      this.prevOperator = row.Condition;
      console.log(this.prevOperator);
      this.editingTableRow = row;
    },

    cancelOperatorEdit() {
      this.editingTableRow.Condition = this.prevOperator;
      this.prevOperator = null;
      this.editingTableRow = {};
      this.operatorDialog = false;
    },

    async onDelete() {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        this.deleteForm();
      } catch (err) {
        console.log(err);
      }
    },

    async getForm() {
      this.loading = true;
      this.lookupItem = null;
      try {
        let response = await this.$api.get('/Lookup/GetLookupTableItem?id=' + this.lookupId);

        if (!response.data) {
          const msg = 'Unable to load lookup item with id ' + this.lookupId;
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        var data = response.data;
        this.lookupItem = data;
        if (this.lookupItem.TableName) this.getLookupTableFields();
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async saveData() {
      this.updateForm();
    },

    async updateForm() {
      try {
        let response = await this.$api.post('/Lookup/UpdateLookup', this.lookupItem);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Penstion element was saved',
        });
        if (this.lookupItem.CalcId === 0) {
          this.$emit('reloadList', 'update', response.data.Value);
        } else {
          this.$emit('reloadList', 'update');
          this.getForm();
        }
      } catch (err) {
        console.error(err);
      }
    },

    async deleteForm() {
      try {
        await this.$api.delete('/Lookup/DeleteLookup?id=' + this.lookupId);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Element is deleted',
        });
        this.$emit('reloadList', 'delete');
      } catch (err) {
        console.error(err);
      }
    },

    async getAvailableInputs() {
      this.loading = true;
      try {
        let response = await this.$api.get(
          `/Calculation/GetCalculationItems?planId=0&groupId=0&calcType=Inputs`
        );
        var data = response.data;
        if (data.length)
          this.availableInputs = data.map((item) => ({
            lookupId: this.lookupId,
            InputId: item.Id,
            InputOrder: 0,
            DefaultValue: '',
            Name: item.ShortName,
          }));
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async getSourceList() {
      this.loading = true;
      try {
        let response = await this.$api.get(`/Lookup/GetLookupTables`);

        this.sourceList = response.data;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async getLookupTableFields() {
      this.loadingTableFields = true;
      try {
        let response = await this.$api.get(
          `/Lookup/GetLookupTableFields?tableName=${this.tableName}`
        );

        this.fieldsList = response.data;
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingTableFields = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.calcFormsForm {
  th {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
    height: 32px;
  }
  td {
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.87);
    text-align: center;
    height: 32px;
  }
  tr:not(:first-child):hover {
    cursor: pointer;
    background-color: #f2f2f2;
  }
  .bd-x {
    border-left: thin solid rgba(0, 0, 0, 0.12);
    border-right: thin solid rgba(0, 0, 0, 0.12);
  }
  .bd-l {
    border-left: thin solid rgba(0, 0, 0, 0.12);
  }
  .bd-r {
    border-right: thin solid rgba(0, 0, 0, 0.12);
  }
  .bd-b {
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
  }
  .bd-t {
    border-top: thin solid rgba(0, 0, 0, 0.12);
  }
}
</style>
